<template>
  <div class="animated fadeIn mt-3">
    <b-row>
      <b-col class="p-0">
        <b-card :no-body="!isCollapsed">
          <div slot="header">
            <strong v-text="$lang.exercise_competency_selector" />
            <b-button
              @click="isCollapsed = !isCollapsed"
              variant="success"
              class="float-right"
              size="sm"
            >
              <mdicon v-if="isCollapsed" name="chevron-up" />
              <mdicon v-else name="chevron-down" />
            </b-button>
          </div>
          <b-collapse :visible="isCollapsed">
            <LoadingSpinner :loading="parentLoading" />
            <table
              v-if="!parentLoading"
              class="table table-bordered table-responsive text-center bg_white"
            >
              <thead role="rowgroup" class="text-center">
                <tr role="row">
                  <th class="empty position-relative p-0">
                    <div class="text-primary text-right pr-2">
                      <strong v-text="$lang.exercises" />
                      <span
                        v-if="isAdmin(permissions) && competencies.length"
                        class="text-success cursor_pointer ml-1"
                        @click="addExercise()"
                      >
                        <mdicon name="plus-circle" size="20" />
                      </span>
                    </div>
                    <svg height="100%" width="100%">
                      <line
                        x1="0"
                        y1="100%"
                        x2="100%"
                        y2="0"
                        style="stroke: rgb(178 186 193); stroke-width: 2"
                      />
                    </svg>
                    <div class="text-dark pl-2">
                      <strong
                        v-text="`${$lang.competencies} - ${$lang.weight}`"
                      />
                      <span
                        v-if="isAdmin(permissions)"
                        class="text-success cursor_pointer ml-1"
                        @click="addCompetency()"
                      >
                        <mdicon name="plus-circle" size="20" />
                      </span>
                    </div>
                  </th>
                  <th
                    class="text-center text-dark align-middle bg-blue-light"
                    v-for="(exercise, exerciseIndex) in exercises"
                    :key="exerciseIndex"
                    role="columnheader"
                    scope="col"
                    @dblclick="
                      exercise.id && enableEdit(exerciseIndex, 'exercise')
                    "
                  >
                    <b-input
                      v-if="isEnable(exerciseIndex, 'exercise')"
                      autofocus
                      type="text"
                      v-model="exercise.nickname"
                      @keypress.enter="disableEdit(exerciseIndex, 'exercise')"
                      @blur="disableEdit(exerciseIndex, 'exercise')"
                    />
                    <strong
                      v-else
                      class="text-primary"
                      v-b-tooltip.hover
                      :title="exercise.id ? $lang.dabbleClickTooltip : ''"
                      v-text="pathOr(null, ['nickname'], exercise)"
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(competency, competencyIndex) in competencies"
                  :key="competencyIndex"
                >
                  <td
                    class="text-left text-dark"
                    @dblclick="showEditCompetencyModal(competency)"
                  >
                    <span v-b-tooltip.hover :title="$lang.dabbleClickTooltip">
                      <i
                        class="fa fa-circle"
                        :style="`color: ${pathOr(
                          'black',
                          ['reference', 'color'],
                          competency
                        )}`"
                        v-b-tooltip.hover
                        :title="
                          pathOr('', ['reference', 'caption'], competency)
                        "
                      />
                      <strong
                        class="ml-2"
                        v-text="pathOr('', ['nickname'], competency)"
                      />
                      <strong
                        v-if="+pathOr(1, ['competency_weight'], competency) > 1"
                        class="ml-2"
                        v-text="
                          `- ${pathOr('', ['competency_weight'], competency)}`
                        "
                      />
                    </span>
                  </td>

                  <template
                    v-for="(exercise, exerciseIndex) in competency.exercises"
                  >
                    <td
                      v-if="exercise.status"
                      :key="exerciseIndex"
                      class="align-middle font-weight-bold"
                    >
                      <div
                        v-if="exercise.id"
                        class="d-flex align-items-center justify-content-around"
                      >
                        <b-form-checkbox v-model="exercise.assigned" />
                        <b-button
                          v-if="
                            exercise.selected_indicator_count ||
                            exercise.competency_indicator_count
                          "
                          variant="success"
                          v-text="
                            `${exercise.selected_indicator_count}/${exercise.competency_indicator_count}`
                          "
                          v-b-tooltip.hover
                          :title="$lang.authentication_indicator_count"
                          @click="showIndicators(competency, exercise)"
                        />
                        <div v-else v-text="'empty'" class="opacity-0" />
                      </div>
                      <div v-else>
                        <b-button
                          :variant="
                            getCoverageVariant(exercise.coverage_percent)
                          "
                          v-text="`${exercise.coverage_percent}%`"
                          @click="changePercent(competency)"
                        />
                      </div>
                    </td>
                    <td v-else :key="exerciseIndex" class="opacity-9" />
                  </template>
                </tr>
              </tbody>
            </table>
          </b-collapse>
          <div slot="footer" class="form-group form-btn" v-if="isCollapsed">
            <b-button
              variant="success"
              v-if="isAdmin(permissions)"
              :disabled="generateItems.length <= 0"
              :class="{ 'cursor--not-allowed': generateItems.length <= 0 }"
              @click="submit"
            >
              {{ $lang.submitButton }}
              <mdicon name="check" />
            </b-button>
          </div>
        </b-card>
      </b-col>
    </b-row>
    <b-modal
      no-close-on-backdrop
      variant="primary"
      header-bg-variant="primary"
      content-class="border-primary"
      v-model="editCompetencyModal"
      size="lg"
      hide-header
      hide-footer
    >
      <b-card no-body class="overflow-hidden">
        <div slot="header" class="bg-light py-2 fs_1rem">
          <strong
            v-text="`${$lang.edit} ${$lang.competency}-${$lang.weight}`"
          />
        </div>
        <b-row class="mx-0 mt-3">
          <b-col cols="12" md="12" class="mb-3 pr-4">
            {{ `${$lang.competency_original_name}: ${originalCompetencyName}` }}
          </b-col>
          <b-col cols="12" md="12" class="mb-3">
            <b-form-group class="mb-0" :label="`${$lang.competency_nickname}:`">
              <requiredComponent
                slot="label"
                :label="`${$lang.competency_nickname}:`"
                :rules="$v.currentCompetencyEditing.nickname.$params"
              />
              <b-form-input
                v-model="$v.currentCompetencyEditing.nickname.$model"
                type="text"
                autocomplete="off"
                :state="
                  $v.currentCompetencyEditing.nickname.$dirty
                    ? !$v.currentCompetencyEditing.nickname.$error
                    : null
                "
              />
              <b-form-invalid-feedback
                :state="
                  $v.currentCompetencyEditing.nickname.$dirty
                    ? !$v.currentCompetencyEditing.nickname.$error
                    : null
                "
              >
                {{ $lang.error_required | replace($lang.competency_nickname) }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="12">
            <b-form-group class="mb-0" :label="`${$lang.competency_weight}:`">
              <requiredComponent
                slot="label"
                :label="`${$lang.competency_weight}:`"
                :rules="$v.currentCompetencyEditing.competency_weight.$params"
              />
              <b-form-input
                v-model="$v.currentCompetencyEditing.competency_weight.$model"
                type="text"
                autocomplete="off"
                :state="
                  $v.currentCompetencyEditing.competency_weight.$dirty
                    ? !$v.currentCompetencyEditing.competency_weight.$error
                    : null
                "
              />
              <b-form-invalid-feedback
                :state="
                  $v.currentCompetencyEditing.competency_weight.$dirty
                    ? !$v.currentCompetencyEditing.competency_weight.$error
                    : null
                "
              >
                {{ $lang.error_required | replace($lang.competency_weight) }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row class="my-3 mx-0">
          <b-col class="flex-end">
            <b-button
              @click="editCompetencyModal = false"
              type="reset"
              variant="outline-danger"
              v-text="$lang.cancelButton"
              class="ml-2"
            />
            <b-button
              @click="editCompetency"
              variant="success"
              v-text="$lang.submitButton"
            />
          </b-col>
        </b-row>
      </b-card>
    </b-modal>
    <b-modal
      no-close-on-backdrop
      variant="primary"
      header-bg-variant="primary"
      content-class="border-primary"
      v-model="indicatorModal.show"
      size="xl"
      hide-header
      ok-only
    >
      <LoadingSpinner
        :loading="indicatorModal.loading"
        :text="$lang.updating_data"
      />
      <DragTable
        v-show="!indicatorModal.loading"
        :prePage="20"
        :url="template_competencies_indicator_url"
        ref="searchActivity"
        :title="$lang.change_indicator_competency_exercise"
        :keyLang="`template_competency_select_fields`"
        :order_url="`activity/order`"
        :editRoute="false"
        :hasMoving="false"
        :order_by="`value:desc`"
        :showCheckbox="true"
        :removeLabel="$lang.exercise_remove"
        :showAlert="true"
        :titleAlert="selectIndicatorAlert"
        @changeCheckbox="itemSelected"
      />
      <template v-slot:modal-footer>
        <b-button
          class="ml-2"
          size="md"
          variant="outline-danger"
          @click="closeIndicatorModal"
        >
          <mdicon name="close" size="20" />
          {{ $lang.close }}
        </b-button>
      </template>
    </b-modal>
    <b-modal
      no-close-on-backdrop
      v-model="view_competencies_indicator_percent"
      size="xl"
      :title="`${$lang.competency}: ${pathOr(
        '',
        ['caption'],
        currentCompetency
      )}`"
      ok-only
    >
      <ExerciseCompetencySelectorComponent
        :showPermissionButton="isAdmin(permissions)"
        :url="competencies_indicator_percent_url"
      />
      <template v-slot:modal-footer>
        <b-button
          class="ml-2"
          size="md"
          variant="outline-danger"
          @click="view_competencies_indicator_percent = false"
        >
          <mdicon name="close" size="20" />
          {{ $lang.close }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
<script>
import LoadingSpinner from "@/components/base/LoadingSpinner";
import ExerciseCompetencySelectorComponent from "./exerciseCompetencySelectorComponent.vue";
import DragTable from "@/components/base/dragTable";
import { required } from "vuelidate/lib/validators";
import { pathOr, includes } from "ramda";
import { generateApiUrl, isAdmin } from "@/shared/utils";

export default {
  name: "competency-exercise-table",
  components: {
    LoadingSpinner,
    ExerciseCompetencySelectorComponent,
    DragTable,
  },
  validations: {
    currentCompetencyEditing: {
      nickname: { required },
      competency_weight: { required },
    },
  },
  data() {
    return {
      loading: false,
      editItems: { competency: [], exercise: [] },
      currentCompetency: null,
      view_competencies_indicator_percent: false,
      competencies_indicator_percent_url: null,
      view_template_competencies_indicator: false,
      template_competencies_indicator_url: null,
      competency_selected: null,
      exercise_selected: null,
      indicatorModal: {
        loading: false,
        show: false,
      },
      isCollapsed: true,
      selectIndicatorAlert: "",
      currentCompetencyEditing: {
        id: null,
        nickname: null,
        competency_weight: null,
      },
      editCompetencyModal: false,
      originalCompetencyName: "",
    };
  },
  props: {
    id: {
      type: [String, Number],
      default: null,
    },
    permissions: {
      type: Array,
      default: () => [],
    },
    template_id: {
      type: [String, Number],
      default: null,
    },
    parentLoading: {
      type: Boolean,
      default: false,
    },
    competencies: {
      type: Array,
      default: () => [],
    },
    exercises: {
      type: Array,
      default: () => [],
    },
  },
  created() {
    this.fetchData();
  },
  computed: {
    generateItems() {
      const items = [];
      for (const competency of this.competencies) {
        for (const exercise of competency.exercises) {
          const exerciseStatus = pathOr(false, ["status"], exercise);
          const exerciseId = pathOr(null, ["id"], exercise);
          const exerciseFind = this.exercises.find(
            (item) => item.id === exerciseId
          );
          if (exerciseStatus && exerciseId) {
            const item = {
              competency_id: pathOr(null, ["id"], competency),
              competency_nickname: pathOr(null, ["nickname"], competency),
              competency_weight: pathOr(
                null,
                ["competency_weight"],
                competency
              ),
              exercise_id: pathOr(null, ["id"], exercise),
              exercise_nickname: pathOr(null, ["nickname"], exerciseFind),
              selected: pathOr(0, ["assigned"], exercise) ? 1 : 0,
            };
            items.push(item);
          }
        }
      }
      return items;
    },
  },
  methods: {
    pathOr,
    includes,
    generateApiUrl,
    isAdmin,
    enableEdit(index, type = "competency") {
      this.editItems[type].push(index);
    },
    isEnable(index, type = "competency") {
      return includes(index, this.editItems[type]);
    },
    disableEdit(index, type = "competency") {
      this.editItems[type] = this.editItems[type].filter((x) => x != index);
    },
    generateExerciseItem(item) {
      const caption = pathOr(false, ["caption"], item);
      return {
        assigned: pathOr(false, ["assigned"], item),
        caption,
        competency_indicator_count: pathOr(
          0,
          ["competency_indicator_count"],
          item
        ),
        id: pathOr(0, ["id"], item),
        nickname: pathOr(caption, ["nickname"], item),
        selected_indicator_count: pathOr(0, ["selected_indicator_count"], item),
        status: pathOr(true, ["status"], item),
        coverage_percent: pathOr(0, ["coverage_percent"], item),
      };
    },
    getCoverageVariant(percent) {
      if (percent <= 25) {
        return "danger";
      }
      if (percent <= 50) {
        return "warning";
      }
      if (percent <= 75) {
        return "info";
      }
      if (percent <= 100) {
        return "primary";
      }
    },
    changePercent(competency) {
      this.currentCompetency = competency;
      /* const exercises = pathOr([], ["exercises"], competency);
      const exercise_id = exercises
        .filter((exercise) => exercise.assigned)
        .map((x) => `exercise_id[]=${x.id}`)
        .join("&"); */
      const competencyId = pathOr(null, ["id"], competency);
      this.competencies_indicator_percent_url = generateApiUrl(
        ["kanoon", "competency_coverage_percent"],
        [this.id, competencyId]
      );
      this.view_competencies_indicator_percent = true;
    },
    addCompetency() {
      this.$emit("addCompetency");
    },
    addExercise() {
      this.$emit("addExercise", this.competencies);
    },
    getCompetencyStyle(color) {
      return {
        color: color,
      };
    },
    showIndicators(competency, exercise) {
      const competencyId = pathOr(null, ["id"], competency);
      const competencyNickname = pathOr(null, ["nickname"], competency);
      const exerciseId = pathOr(null, ["id"], exercise);
      const exerciseNickname = pathOr(null, ["nickname"], exercise);
      this.template_competencies_indicator_url = generateApiUrl(
        ["kanoon", "template_competency_indicator2"],
        [this.template_id, competencyId],
        { "exercise_id[]": exerciseId }
      );
      this.competency_selected = competency;
      this.exercise_selected = exercise;
      this.selectIndicatorAlert = this.$lang.selectIndicatorAlert
        .replace("{0}", competencyNickname)
        .replace("{1}", exerciseNickname);
      this.indicatorModal.show = true;
    },
    fetchData() {
      this.$emit("fetchData");
    },
    closeIndicatorModal() {
      this.indicatorModal.show = false;
      this.fetchData();
    },
    async itemSelected(item, event, callback) {
      try {
        this.indicatorModal.loading = true;
        const competencyId = pathOr(null, ["id"], this.competency_selected);
        const exerciseId = pathOr(null, ["id"], this.exercise_selected);
        const indicator_id = pathOr(null, ["id"], item);
        const url = generateApiUrl(
          ["kanoon", "template_indicator_exercise_indicator"],
          [this.template_id, competencyId, exerciseId]
        );
        const payload = { indicator_id };
        const response = await this.$httpService.put(url, payload);
        const exerciseDesignId = pathOr(null, ["data", "data"], response);
        callback("exercise_design_id", exerciseDesignId);
        this.$notify.success(response.data.message);
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.indicatorModal.loading = false;
      }
    },
    submit() {
      /* const items = [];
      for (const competency of this.competencies) {
        for (const exercise of competency.exercises) {
          const exerciseStatus = pathOr(false, ["status"], exercise);
          const exerciseId = pathOr(null, ["id"], exercise);
          const exerciseFind = this.exercises.find(
            (item) => item.id === exerciseId
          );
          if (exerciseStatus && exerciseId) {
            const item = {
              competency_id: pathOr(null, ["id"], competency),
              competency_nickname: pathOr(null, ["nickname"], competency),
              competency_weight: pathOr(
                null,
                ["competency_weight"],
                competency
              ),
              exercise_id: pathOr(null, ["id"], exercise),
              exercise_nickname: pathOr(null, ["nickname"], exerciseFind),
              selected: pathOr(0, ["assigned"], exercise) ? 1 : 0,
            };
            items.push(item);
          }
        }
      } */
      this.$emit("submit", this.generateItems);
    },
    showEditCompetencyModal(competency) {
      this.editCompetencyModal = true;
      const { exercises, reference, caption, ...otherData } = competency;
      this.originalCompetencyName = caption;
      this.currentCompetencyEditing = { ...otherData };
    },
    editCompetency() {
      this.$v.$touch();
      if (this.$v.$anyError) return;

      this.competencies.map((item) => {
        const check = this.currentCompetencyEditing.id === item.id;
        if (check) {
          item.nickname = pathOr(
            "",
            ["nickname"],
            this.currentCompetencyEditing
          );
          item.competency_weight = pathOr(
            1,
            ["competency_weight"],
            this.currentCompetencyEditing
          );
        }
        return item;
      });
      this.editCompetencyModal = false;
    },
  },
};
</script>

<style lang="scss">
.text-line-left {
  position: absolute;
  left: 0;
  top: 0;
}

.line {
  position: relative;

  &:after {
    position: absolute;
    content: "";
    width: 2px;
    height: 60px;
    background: lightgrey;
    left: 0;
    top: -19px;
    transform: rotate(45deg);
  }
}

.d-content {
  display: contents;
}

.btn-red {
  background: black;
  padding: 2px;
  color: white;
}

.btn-darkslateblue {
  background: darkslateblue;
  padding: 2px;
  color: white;
}

.text-darkslateblue {
  color: darkslateblue;
}

.text-competencies {
  position: absolute;
  top: 5%;
  left: 5%;
}

.text-exercises {
  position: absolute;
  right: 5%;
  bottom: 5%;
}

.opacity-0 {
  opacity: 0;
}

.opacity-9 {
  opacity: 0.9;
  background: #607d8b8a !important;
}
.cursor_not_allowed {
  cursor: not-allowed;
}
</style>
